import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

interface ExchangeRejectionModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (reason: string, notes: string | null) => void
  isLoading?: boolean
}

const REJECTION_REASONS = [
  'insufficient_points',
  'invalid_request',
  'policy_violation',
  'reward_unavailable',
  'fraud_suspicion',
  'other',
] as const

export function ExchangeRejectionModal({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
}: ExchangeRejectionModalProps) {
  const { t } = useTranslation()
  const [selectedReason, setSelectedReason] = useState<string>('')
  const [notes, setNotes] = useState('')

  const handleConfirm = () => {
    if (!selectedReason) return
    onConfirm(selectedReason, notes.trim() || null)
    setSelectedReason('')
    setNotes('')
  }

  const handleClose = () => {
    setSelectedReason('')
    setNotes('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('exchanges.detail.rejectionModal.title')}</DialogTitle>
          <DialogDescription>{t('exchanges.detail.rejectionModal.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t('exchanges.detail.rejectionModal.reasonLabel')}</Label>
            <Select value={selectedReason} onValueChange={setSelectedReason}>
              <SelectTrigger>
                <SelectValue placeholder={t('exchanges.detail.rejectionModal.reasonLabel')} />
              </SelectTrigger>
              <SelectContent>
                {REJECTION_REASONS.map((reason) => (
                  <SelectItem key={reason} value={reason}>
                    {t(`exchanges.detail.rejectionModal.reasons.${reason}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">{t('exchanges.detail.rejectionModal.notesLabel')}</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('exchanges.detail.rejectionModal.notesPlaceholder')}
              className="min-h-[80px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {t('exchanges.detail.rejectionModal.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!selectedReason || isLoading}
          >
            {isLoading ? t('exchanges.detail.rejectionModal.rejecting') : t('exchanges.detail.rejectionModal.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}